package com.sddamico.draftmachine.components.pwa

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.glowBrass
import com.sddamico.draftmachine.ds.motion.LocalExiting
import com.sddamico.draftmachine.ds.motion.Presence
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.ds.roleSmall
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event

private const val DISMISS_KEY = "dm-install-dismissed"

/**
 * The PWA install moment (design frame 1r). Registers the service worker, captures the
 * `beforeinstallprompt` event, and surfaces a bottom sheet inviting the user to bolt the app to
 * their home screen. Mount from the authenticated dashboard only.
 *
 * Visibility: shows when an install prompt is available (Chromium) or the client is iOS, unless the
 * user previously dismissed it (`localStorage["dm-install-dismissed"]`). The `?pwa=1` query param is
 * a test hook that forces the sheet open (install rarely fires in automation) and intentionally
 * overrides a stored dismissal.
 */
@Composable
fun InstallPrompt() {
  val forced = remember { window.location.search.contains("pwa=1") }
  val isIos = remember { detectIos() }
  val alreadyInstalled = remember { detectStandalone() }

  var installAvailable by remember { mutableStateOf(false) }
  var deferredPrompt by remember { mutableStateOf<Any?>(null) }
  var dismissed by remember { mutableStateOf(localStorage.getItem(DISMISS_KEY) == "1") }
  var closed by remember { mutableStateOf(false) }

  DisposableEffect(Unit) {
    registerServiceWorker()
    val handler: (Event) -> Unit = { e ->
      e.preventDefault()
      deferredPrompt = e
      installAvailable = true
    }
    window.addEventListener("beforeinstallprompt", handler)
    onDispose { window.removeEventListener("beforeinstallprompt", handler) }
  }

  val show = when {
    closed -> false
    alreadyInstalled && !forced -> false
    forced -> true
    dismissed -> false
    else -> installAvailable || isIos
  }

  Presence(show, exitMillis = DmMotion.sheetExitMs) {
    InstallSheet(
      showInstallButton = !isIos,
      onInstall = {
        deferredPrompt?.asDynamic()?.prompt()
        closed = true
      },
      onDismiss = {
        localStorage.setItem(DISMISS_KEY, "1")
        dismissed = true
        closed = true
      },
    )
  }
}

@Composable
private fun InstallSheet(
  showInstallButton: Boolean,
  onInstall: () -> Unit,
  onDismiss: () -> Unit,
) {
  val exiting = LocalExiting.current
  Div(
    attrs = Modifier
      .position(Position.Fixed)
      .zIndex(1000)
      .displayFlex()
      .justifyContent(JustifyContent.Center)
      .alignItems(AlignItems.FlexEnd)
      .styleModifier {
        property("inset", "0")
        if (exiting) property("pointer-events", "none")
      }
      .toAttrs { attr("role", "dialog"); attr("aria-modal", "true") }
  ) {
    // Scrim over the machine room.
    Div(
      attrs = Modifier
        .position(Position.Absolute)
        .backgroundColor(Color.rgba(13, 16, 19, 0.55f))
        .styleModifier {
          property("inset", "0")
          property(
            "animation",
            if (exiting) "dm-fade-out ${DmMotion.sheetExitMs}ms ${DmMotion.easeOut} both"
            else "dm-fade-in 280ms ${DmMotion.easeOut} both",
          )
        }
        .toAttrs()
    ) {}

    // Bottom sheet.
    Div(
      attrs = Modifier
        .position(Position.Relative)
        .zIndex(1)
        .fillMaxWidth()
        .maxWidth(430.px)
        .backgroundColor(DmColor.surfacePanel)
        .padding(top = 10.px, leftRight = 20.px, bottom = 24.px)
        .boxShadowRaw("0 -14px 40px rgba(0,0,0,.6)")
        .styleModifier {
          property("border-top", "1px solid #4B5A68")
          property("border-radius", "18px 18px 0 0")
          property(
            "animation",
            if (exiting) "dm-tray-out ${DmMotion.sheetExitMs}ms ${DmMotion.easeExit} both"
            else "dm-tray-in ${DmMotion.sheetEnterMs}ms ${DmMotion.easeSnap} both",
          )
        }
        .toAttrs()
    ) {
      GrabHandle()
      HeaderRow()
      Benefits()
      Actions(showInstallButton, onInstall, onDismiss)
      if (showInstallButton) IosHint()
    }
  }
}

@Composable
private fun GrabHandle() {
  Div(
    attrs = Modifier
      .displayBlock()
      .width(38.px).height(4.px).borderRadius(99.px)
      .backgroundColor(DmColor.steel600)
      .styleModifier { property("margin", "0 auto 18px") }
      .toAttrs()
  ) {}
}

@Composable
private fun HeaderRow() {
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(14.px)
      .margin(bottom = 14.px).toAttrs()
  ) {
    // Brass app tile with the gear mark (mirrors the DS Logo mark treatment).
    Div(
      attrs = Modifier
        .width(56.px).height(56.px).borderRadius(13.px).flex("0 0 auto")
        .displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
        .glowBrass()
        .styleModifier { property("background-image", "linear-gradient(160deg, #E6B183, #9E5F30)") }
        .toAttrs()
    ) {
      Icon("gear-six", IconWeight.Fill, Modifier.fontSize(30.px).color(DmColor.steel980))
    }
    Div(attrs = Modifier.minWidth(0.px).toAttrs()) {
      SpanText("Bolt it to your home screen", Modifier.roleH3().displayBlock().color(DmColor.text))
      SpanText(
        "Full-screen, one tap from your pick.",
        Modifier.roleSmall().displayBlock().color(DmColor.textMuted).margin(top = 2.px),
      )
    }
  }
}

@Composable
private fun Benefits() {
  Div(
    attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(10.px)
      .margin(bottom = 16.px).toAttrs()
  ) {
    BenefitRow("bell-ringing", DmColor.glass400, "A push the moment you're on the clock")
    BenefitRow("lightning", DmColor.brass400, "Opens straight to the pick that's waiting")
  }
}

@Composable
private fun BenefitRow(icon: String, iconColor: Color, text: String) {
  Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px).toAttrs()) {
    Icon(icon, IconWeight.Bold, Modifier.fontSize(18.px).color(iconColor).flex("0 0 auto"))
    SpanText(text, Modifier.roleSmall().color(DmColor.textSecondary))
  }
}

@Composable
private fun Actions(showInstallButton: Boolean, onInstall: () -> Unit, onDismiss: () -> Unit) {
  Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
    if (showInstallButton) {
      Button(
        "Install the app", variant = ButtonVariant.Primary, icon = "download-simple",
        fullWidth = true, onClick = onInstall,
      )
    } else {
      // iOS has no beforeinstallprompt, so guide the manual Share -> Add flow in place of a button.
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
          .gap(8.px).padding(topBottom = 12.px, leftRight = 14.px)
          .border(1.px, LineStyle.Solid, DmColor.borderStrong).borderRadius(DmEffects.radiusMd)
          .backgroundColor(DmColor.surfaceInset)
          .toAttrs()
      ) {
        Icon("export", IconWeight.Bold, Modifier.fontSize(18.px).color(DmColor.brass400).flex("0 0 auto"))
        SpanText("Tap Share, then Add to Home Screen", Modifier.roleSmall().color(DmColor.text))
      }
    }
    Button("Not now", variant = ButtonVariant.Ghost, fullWidth = true, onClick = onDismiss)
  }
}

@Composable
private fun IosHint() {
  Div(
    attrs = Modifier.fillMaxWidth().margin(top = 12.px)
      .styleModifier { property("text-align", "center") }.toAttrs()
  ) {
    SpanText("On iPhone: Share → Add to Home Screen", Modifier.dmStamp().color(DmColor.steel500))
  }
}

// ---- Platform helpers ----

private fun registerServiceWorker() {
  try {
    val sw = window.navigator.asDynamic().serviceWorker
    if (sw != null) sw.register("/sw.js")
  } catch (_: Throwable) {
  }
}

private fun detectIos(): Boolean {
  val ua = window.navigator.userAgent
  return ua.contains("iPhone") || ua.contains("iPad") || ua.contains("iPod")
}

private fun detectStandalone(): Boolean {
  val displayMode = window.matchMedia("(display-mode: standalone)").matches
  val iosStandalone = window.navigator.asDynamic().standalone == true
  return displayMode || iosStandalone
}
